// "For a request?" on the receipt review page: the buyer ties a card receipt to
// the requests it paid for, from the receipt's side. Drawn only for orderers,
// and quiet: a field among the receipt's fields, not a panel of its own.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../components/Toast/ToastContext';
import AnchoredPopover from './AnchoredPopover';
import StatusChip from './StatusChip';
import { buttonClass } from '../components/ui/Button';
import Checkbox from '../components/ui/Checkbox';
import Tooltip from '../components/ui/Tooltip';
import Alert from '../components/ui/Alert';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import { useExiting } from '../components/ui/motion';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { errorMessage, useRequisitionsApi, type LinkableRequisition } from './requisitionsApi';

interface Props {
  receiptPid: string;
  /** The host page's label and field classes, so this sits like its neighbors. */
  labelClassName: string;
  fieldClassName: string;
  /**
   * A link may fill the receipt's class and show from the request, on the
   * server. The host reloads those two so its own save does not blank them.
   */
  onLinked?: () => void;
}

const ReceiptRequestField: React.FC<Props> = ({ receiptPid, labelClassName, fieldClassName, onLinked }) => {
  const reqApi = useRequisitionsApi();
  const toast = useToast();
  const [shown, setShown] = useState(false);
  const [rows, setRows] = useState<LinkableRequisition[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const loadLinkable = async () => {
    setFailed(false);
    try {
      setRows(await reqApi.linkable(receiptPid));
    } catch (err) {
      const status = (err as { status?: number })?.status;
      // Not a party to this receipt: the field is not theirs to use.
      if (status === 403 || status === 404) setShown(false);
      else setFailed(true);
    }
  };

  useEffect(() => {
    let live = true;
    setShown(false);
    setRows(null);
    reqApi
      .me()
      .then((me) => {
        if (!live || !me?.is_orderer) return;
        setShown(true);
        loadLinkable();
      })
      .catch(() => {});
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptPid]);

  const linked = useMemo(() => (rows || []).filter((r) => r.linked), [rows]);
  const chips = useExiting(rows ? linked : null, (r) => r.id);

  if (!shown) return null;

  const open = (rows || []).filter((r) => !r.linked);

  const link = async (): Promise<boolean> => {
    setBusy('link');
    let done = 0;
    try {
      for (const id of picked) {
        await reqApi.linkReceipt(id, receiptPid);
        done += 1;
      }
      const names = (rows || []).filter((r) => picked.includes(r.id)).map((r) => r.req_number);
      toast.success(`Linked to ${names.join(', ')}.`);
      return true;
    } catch (err) {
      const lead = done ? `Linked ${done} of ${picked.length}. ` : '';
      toast.error(lead + errorMessage(err, 'Could not link that request. Try again.'));
      return false;
    } finally {
      if (done) {
        onLinked?.();
        await loadLinkable();
      }
      setBusy(null);
    }
  };

  const unlink = async (r: LinkableRequisition) => {
    setBusy(`unlink-${r.id}`);
    try {
      await reqApi.unlinkReceipt(r.id, receiptPid);
      toast.success(`Unlinked from ${r.req_number}.`);
      await loadLinkable();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not unlink that request. Try again.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div role="group" aria-labelledby="receipt-request-label">
      <span id="receipt-request-label" className={labelClassName}>
        For a request? (optional)
      </span>

      {failed ? (
        <Alert variant="text" onRetry={loadLinkable} retryLabel="Try again" retryTip="Load your requests again" className="text-xs">
          Could not load your requests.
        </Alert>
      ) : rows === null ? (
        <SkeletonGroup label="Loading requests">
          <Skeleton className="h-[34px] w-full" />
        </SkeletonGroup>
      ) : (
        <FadeIn className="space-y-1.5">
          <Reveal show={chips.length > 0}>
            <ul className="flex flex-wrap gap-1.5" aria-label="Linked requests">
              {chips.map(({ item: r, key, leaving }) => (
                <Appear
                  as="li"
                  key={key}
                  show={!leaving}
                  className="inline-flex max-w-full items-center gap-1 rounded-full bg-primary/15 py-0.5 pl-2.5 pr-0.5 text-xs text-primary-ink"
                >
                  <Tooltip label={r.purpose} truncated>
                    <Link
                      to={`/requisitions/${r.id}`}
                      className="min-w-0 truncate hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded"
                    >
                      <span className="font-mono">{r.req_number}</span> {r.purpose}
                    </Link>
                  </Tooltip>
                  <Tooltip label={`Unlink ${r.req_number}`}>
                    <button
                      type="button"
                      aria-label={`Unlink ${r.req_number}`}
                      disabled={busy !== null}
                      onClick={() => unlink(r)}
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full hover:bg-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-40"
                    >
                      <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" className="h-3 w-3">
                        <path
                          fill="currentColor"
                          d="M4.22 4.22a.75.75 0 0 1 1.06 0L8 6.94l2.72-2.72a.75.75 0 1 1 1.06 1.06L9.06 8l2.72 2.72a.75.75 0 1 1-1.06 1.06L8 9.06l-2.72 2.72a.75.75 0 0 1-1.06-1.06L6.94 8 4.22 5.28a.75.75 0 0 1 0-1.06Z"
                        />
                      </svg>
                    </button>
                  </Tooltip>
                </Appear>
              ))}
            </ul>
          </Reveal>

          {open.length > 0 ? (
            <AnchoredPopover
              triggerLabel={linked.length ? 'Add another request' : 'Pick a request'}
              triggerClassName={`${fieldClassName} text-left`}
              tooltip="Ties this receipt to an approved request you bought it for"
              title="Which request did this pay for?"
              align="left"
              width="lg"
              fullWidth
              disabled={busy !== null}
              onOpen={() => setPicked([])}
            >
              {(close) => (
                <div className="space-y-3">
                  <ul className="max-h-64 space-y-1 overflow-y-auto pr-1">
                    {open.map((r) => {
                      const checked = picked.includes(r.id);
                      return (
                        <li key={r.id}>
                          <Checkbox
                            fill
                            checked={checked}
                            onChange={() =>
                              setPicked((p) => (checked ? p.filter((x) => x !== r.id) : [...p, r.id]))
                            }
                            className={`rounded-md px-2.5 py-2 ${checked ? 'bg-primary/15' : 'hover:bg-surface-container-highest'}`}
                            label={
                              <span className="flex items-center gap-3">
                                <span className="min-w-0 flex-1">
                                  <Tooltip label={r.purpose} truncated>
                                    <span className="block truncate text-sm text-on-surface">
                                      {r.purpose}
                                    </span>
                                  </Tooltip>
                                  <span className="block text-xs text-on-surface-variant">
                                    <span className="font-mono">{r.req_number}</span>
                                    {r.requester ? `, asked by ${r.requester.name}` : ''}
                                  </span>
                                </span>
                                <StatusChip status={r.status} />
                              </span>
                            }
                          />
                        </li>
                      );
                    })}
                  </ul>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      disabled={busy !== null || picked.length === 0}
                      onClick={async () => {
                        if (await link()) close();
                      }}
                      className={buttonClass('filled', 'sm')}
                    >
                      {busy === 'link' ? 'Linking…' : picked.length > 1 ? `Link ${picked.length} requests` : 'Link request'}
                    </button>
                  </div>
                </div>
              )}
            </AnchoredPopover>
          ) : (
            !linked.length && (
              <EmptyState variant="line" dense>No approved requests are waiting for a receipt.</EmptyState>
            )
          )}
        </FadeIn>
      )}
    </div>
  );
};

export default ReceiptRequestField;
